import React, { useState, useRef, useEffect } from "react";
import { IconButton } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import DescriptionIcon from "@mui/icons-material/Description";

const FileUploadInput = ({ value, onChange, readOnly, style, rowId }) => {
  const fileInputRef = useRef(null);
  // Handle both File objects and string URLs
  const getFileName = (val) => {
    if (!val) return null;
    if (typeof val === "string") return val.split("/").pop();
    if (val instanceof File) return val.name;
    return null;
  };
  const [fileName, setFileName] = useState(getFileName(value));

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "text/csv",
      ];
      const allowedExtensions = [
        "jpg",
        "jpeg",
        "png",
        "pdf",
        "doc",
        "docx",
        "xlsx",
        "csv",
      ];
      const fileExtension = file.name.split(".").pop().toLowerCase();

      if (
        !allowedTypes.includes(file.type) &&
        !allowedExtensions.includes(fileExtension)
      ) {
        alert(
          "Invalid file type. Allowed: jpg, jpeg, png, pdf, doc, docx, xlsx, csv"
        );
        return;
      }

      // Validate file size (50MB max)
      if (file.size > 50 * 1024 * 1024) {
        alert("File too large. Maximum size is 50MB.");
        return;
      }

      setFileName(file.name);
      if (onChange) {
        onChange(file, rowId);
      }
    }
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    setFileName(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    if (onChange) {
      onChange(null, rowId);
    }
  };

  const handleClick = () => {
    if (!readOnly && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Update fileName when value prop changes
  useEffect(() => {
    const newFileName = getFileName(value);
    setFileName(newFileName);
  }, [value]);

  const handleFileClick = (e) => {
    e.stopPropagation();
    if (value && typeof value === "string") {
      window.open(value, "_blank");
    }
  };

  if (readOnly) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "4px",
          padding: "4px 4px",
          width: "100%",
          boxSizing: "border-box",
          overflow: "hidden",
          ...style,
        }}
      >
        {value ? (
          <>
            <DescriptionIcon fontSize="small" style={{ flexShrink: 0 }} />
            <span
              style={{
                fontSize: "12px",
                color: "#666",
                flex: 1,
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                cursor: typeof value === "string" ? "pointer" : "default",
                textDecoration: typeof value === "string" ? "underline" : "none",
              }}
              onClick={handleFileClick}
              title={typeof value === "string" ? "Click to open file" : fileName || getFileName(value) || ""}
            >
              {fileName || getFileName(value) || "-"}
            </span>
          </>
        ) : (
          <span style={{ fontSize: "12px", color: "#999" }}>-</span>
        )}
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "4px",
        padding: "4px 4px",
        cursor: "pointer",
        width: "100%",
        boxSizing: "border-box",
        overflow: "hidden",
        ...style,
      }}
      onClick={handleClick}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept=".jpg,.jpeg,.png,.pdf,.doc,.docx,.xlsx,.csv"
        style={{ display: "none" }}
        onChange={handleFileSelect}
      />
      {fileName || value ? (
        <>
          <DescriptionIcon fontSize="small" color="primary" style={{ flexShrink: 0 }} />
          <span
            style={{
              fontSize: "12px",
              flex: 1,
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              cursor: typeof value === "string" ? "pointer" : "default",
              textDecoration: typeof value === "string" ? "underline" : "none",
            }}
            onClick={typeof value === "string" ? handleFileClick : undefined}
            title={typeof value === "string" ? "Click to open file" : fileName || getFileName(value) || ""}
          >
            {fileName || getFileName(value) || "Upload..."}
          </span>
          <IconButton
            size="small"
            onClick={handleDelete}
            style={{ 
              padding: "4px",
              flexShrink: 0,
              marginLeft: "2px"
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </>
      ) : (
        <>
          <CloudUploadIcon fontSize="small" color="action" style={{ flexShrink: 0 }} />
          <span style={{ fontSize: "12px", color: "#999" }}>Upload...</span>
        </>
      )}
    </div>
  );
};

export default FileUploadInput;
